import { v4 as uuidV4 } from 'uuid';

import {
  defaultEmailSettings,
  defaultEmailTimezone,
} from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/helpers/email-defaults';
import { Channel } from '@/types/channel.types';
import { EmailTypeEnum, IScheduledEmailCustomizations } from '@/types/email.types';
import { BLAdmin } from '@/types/profile.types';
import { BrandliveEvent } from '@/types/projects.types';
import { Registration } from '@/types/registration.types';
import { SessionTypesEnum } from '@/types/session.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { saveWorkingProjectByFields } from '..';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import getEmailToSend from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/scheduled-emails/get-email-to-send';
import updatePlainTextEmails from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/emails/helpers/use-plain-text-email';

type TUpdateRegistrationEmailParams = {
  workingProject: BrandliveEvent;
  currentChannel: Channel;
  publishedUrl: string;
  user: BLAdmin;
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>;
};

const updateRegistrationEmail = async ({
  workingProject,
  currentChannel,
  publishedUrl,
  user,
  dispatch,
}: TUpdateRegistrationEmailParams) => {
  if (!workingProject?.registration_settings || !user) return;

  const batchId = uuidV4();

  let updatedRegistrationConfirmationEmail: IScheduledEmailCustomizations | undefined;
  const registrationEmailSettings =
    workingProject?.registration_settings?.registration_email_settings ?? defaultEmailSettings;
  const timezone = workingProject?.registration_settings?.registration_email_timezone ?? defaultEmailTimezone;
  const registrationConfirmationSubjectChanged =
    workingProject?.settings?.email?.registrationConfirmationSubjectChanged ?? false;

  // we need to assign to a new variable because `getEmailToSend` overrides the emailContent object
  let updatedSettings: Registration = { ...workingProject.registration_settings };

  try {
    updatedRegistrationConfirmationEmail = await getEmailToSend({
      emailType: EmailTypeEnum.event_registration_email,
      workingProject: {
        ...workingProject,
        registration_settings: updatedSettings,
      },
      currentChannel,
      publishedUrl,
      emailContent: updatedSettings?.registration_confirmation_email ?? {},
      timezone,
      emailSettings: registrationEmailSettings,
      overrideSubject: !registrationConfirmationSubjectChanged, // Don't override subject with the event name if the subject has manually been changed.
      batchId,
    });
  } catch (e) {
    console.error('Error getting registration confirmation email:', e);
  }

  if (!updatedRegistrationConfirmationEmail) return;
  updatedSettings = {
    ...updatedSettings,
    registration_confirmation_email: updatedRegistrationConfirmationEmail,
    registration_email_settings: registrationEmailSettings,
    registration_email_timezone: timezone,
  };

  try {
    // update plain text emails if needed
    const updatedCustomizations = await updatePlainTextEmails({
      workingProject: {
        ...workingProject,
        registration_settings: updatedSettings,
      },
      usePlainTextEmail: !!updatedSettings.registration_email_settings?.usePlainTextEmail,
      sessionUuid: undefined,
      channel: currentChannel,
      publishedUrl,
      language: undefined,
      customizations: updatedRegistrationConfirmationEmail,
      emailType: EmailTypeEnum.event_registration_email,
      deliveryDate: undefined,
      showStartTime: !!updatedSettings.registration_email_settings?.showStartTime,
      showAddCalendar: !!updatedSettings.registration_email_settings?.showAddCalendar,
      isAllOnDemand: workingProject.sessions.every((session) => session.session_type === SessionTypesEnum.onDemand),
      isOnDemandSession: false,
    });

    updatedSettings.registration_confirmation_email = updatedCustomizations;

    // await UpdateRegistrationSettings({ projectUuid: workingProject.uuid, updatedSettings });
    // dispatch(updateRegistrationSettingsInRedux({ updatedSettings }));
    dispatch(
      saveWorkingProjectByFields({
        registration_settings: updatedSettings,
        saveId: 'publish-update-registration-email',
      })
    );
  } catch (e) {
    console.error(e);
    toastTrigger(
      `
			<div>
				<h6 class="H6">Conversion Failed</h6>
				<p class="P2">Failed to update registration settings.</p>
			</div>
		`,
      {
        type: 'error',
      }
    );
  }
};

export default updateRegistrationEmail;
